import React, { useState, useEffect } from 'react';
import { CustomPartner, CreateCustomPartnerRequest, UpdateCustomPartnerRequest, PartnerType, PartnerTypeLabels, PartnerTypeColors } from '../types/customPartners';
import { customPartnersApi } from '../services/customPartnersApi';
import CustomPartnerForm from '../components/customPartners/CustomPartnerForm';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { ToastContainer } from '../components/common/Toast';
import { Plus, Pencil, Trash2, LayoutGrid, List, Search, Globe, Mail, Phone, MapPin, XCircle, RefreshCw, CheckCircle, Users2, Inbox } from 'lucide-react';
import { usePreferredViewMode, STANDARD_VIEW_MODE_MAP } from '../hooks/usePreferredViewMode';
import '../styles/PageLayout.css';

const CustomPartners: React.FC = () => {
  const [partners, setPartners] = useState<CustomPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = usePreferredViewMode<'list' | 'cards'>('cards', STANDARD_VIEW_MODE_MAP);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<CustomPartner | null>(null);
  const [isFormLoading, setIsFormLoading] = useState(false);

  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; partner: CustomPartner | null }>({
    isOpen: false,
    partner: null
  });

  // Toast
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type: 'success' | 'error' }>>([]);

  const showToast = (message: string, type: 'success' | 'error') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const loadPartners = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await customPartnersApi.getAll(includeInactive);
      // Ensure data is an array
      setPartners(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error loading custom partners:', err);
      setError('Fehler beim Laden der benutzerdefinierten Partner');
      setPartners([]); // Set empty array on error
      showToast('Fehler beim Laden der benutzerdefinierten Partner', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPartners();
  }, [includeInactive]);

  const handleCreate = async (partnerData: CreateCustomPartnerRequest) => {
    try {
      setIsFormLoading(true);
      await customPartnersApi.create(partnerData);
      await loadPartners();
      setIsFormOpen(false);
      showToast('Partner erfolgreich erstellt', 'success');
    } catch (err) {
      console.error('Error creating partner:', err);
      showToast('Fehler beim Erstellen des Partners', 'error');
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleUpdate = async (id: number, partnerData: UpdateCustomPartnerRequest) => {
    try {
      setIsFormLoading(true);
      await customPartnersApi.update(id, partnerData);
      await loadPartners();
      setIsFormOpen(false);
      setEditingPartner(null);
      showToast('Partner erfolgreich aktualisiert', 'success');
    } catch (err) {
      console.error('Error updating partner:', err);
      showToast('Fehler beim Aktualisieren des Partners', 'error');
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleDelete = async (partner: CustomPartner) => {
    try {
      await customPartnersApi.delete(partner.id);
      await loadPartners();
      setDeleteConfirm({ isOpen: false, partner: null });
      showToast('Partner erfolgreich gelöscht', 'success');
    } catch (err) {
      console.error('Error deleting partner:', err);
      showToast('Fehler beim Löschen des Partners', 'error');
    }
  };

  const openCreateForm = () => {
    setEditingPartner(null);
    setIsFormOpen(true);
  };

  const openEditForm = (partner: CustomPartner) => {
    setEditingPartner(partner);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingPartner(null);
  };

  const openDeleteConfirm = (partner: CustomPartner) => {
    setDeleteConfirm({ isOpen: true, partner });
  };

  const closeDeleteConfirm = () => {
    setDeleteConfirm({ isOpen: false, partner: null });
  };

  // Filter partners based on search term and type
  const filteredPartners = Array.isArray(partners) ? partners.filter(partner => {
    const matchesSearch = partner.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (partner.description && partner.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesType = filterType === 'all' || partner.partnerType === filterType;
    return matchesSearch && matchesType;
  }) : [];

  // Get unique partner types for filter dropdown
  const partnerTypes = Array.isArray(partners) ? Array.from(new Set(partners.map(p => p.partnerType))) : [];

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-loading"><p>Lade benutzerdefinierte Partner...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="page-error">
          <h2><XCircle size={16} /> Fehler</h2>
          <p>{error}</p>
          <button className="page-btn page-btn-create" onClick={loadPartners}>
            <RefreshCw size={14} /> Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Controls – two-row Menüband */}
      <div className="page-controls">
        {/* Row 1: Buttons */}
        <div className="page-controls-row">
          <button className="page-btn page-btn-create" onClick={openCreateForm} title="Neuer Partner">
            <Plus size={14} /> Neu
          </button>
          <label className="page-checkbox-label">
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
            Inaktive anzeigen
          </label>
          <div className="page-controls-spacer" />
          <div className="page-view-toggle">
            <button className={`page-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`} onClick={() => setViewMode('cards')} title="Kacheln">
              <LayoutGrid size={14} />
            </button>
            <button className={`page-toggle-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')} title="Liste">
              <List size={14} />
            </button>
          </div>
        </div>
        {/* Row 2: Search + Filters + record count */}
        <div className="page-controls-row">
          <input
            type="text"
            className="page-search-input"
            placeholder="Partner suchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            className="page-filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="all">Alle Typen</option>
            {partnerTypes.map(type => (
              <option key={type} value={type}>
                {PartnerTypeLabels[type as PartnerType] || type}
              </option>
            ))}
          </select>
          <div className="page-controls-spacer" />
          <span className="page-record-count">{filteredPartners.length} / {partners.length} Partner · {partners.filter(p => p.isActive).length} Aktiv</span>
        </div>
      </div>

      {/* Partners Display */}
      {filteredPartners.length === 0 ? (
        <div className="page-empty-state">
          <h3><Inbox size={16} /> Keine Partner gefunden</h3>
          <p>
            {searchTerm || filterType !== 'all'
              ? 'Keine Partner entsprechen den aktuellen Filterkriterien.'
              : 'Sie haben noch keine benutzerdefinierten Partner erstellt.'
            }
          </p>
          {(!searchTerm && filterType === 'all') && (
            <button className="page-btn page-btn-create" onClick={openCreateForm}>
              <Plus size={14} /> Ersten Partner erstellen
            </button>
          )}
        </div>
      ) : (
        <div className={viewMode === 'cards' ? 'page-cards-grid' : 'page-list-container'}>
          {filteredPartners.map(partner => (
            viewMode === 'cards' ? (
              <PartnerCard
                key={partner.id}
                partner={partner}
                onEdit={openEditForm}
                onDelete={openDeleteConfirm}
              />
            ) : (
              <PartnerListItem
                key={partner.id}
                partner={partner}
                onEdit={openEditForm}
                onDelete={openDeleteConfirm}
              />
            )
          ))}
        </div>
      )}

      {/* Modals */}
      {isFormOpen && (
        <CustomPartnerForm
          partner={editingPartner}
          onSubmit={editingPartner ? 
            (data) => handleUpdate(editingPartner.id, data) : 
            handleCreate
          }
          onCancel={closeForm}
          loading={isFormLoading}
        />
      )}

      {deleteConfirm.isOpen && deleteConfirm.partner && (
        <ConfirmDialog
          isOpen={deleteConfirm.isOpen}
          title="Partner löschen"
          message={`Möchten Sie den Partner "${deleteConfirm.partner.name}" wirklich löschen?`}
          onConfirm={() => handleDelete(deleteConfirm.partner!)}
          onCancel={closeDeleteConfirm}
          confirmText="Löschen"
          cancelText="Abbrechen"
          type="danger"
        />
      )}

      <ToastContainer toasts={toasts} onRemoveToast={removeToast} />
    </div>
  );
};

// Partner Card Component
const PartnerCard: React.FC<{
  partner: CustomPartner;
  onEdit: (partner: CustomPartner) => void;
  onDelete: (partner: CustomPartner) => void;
}> = ({ partner, onEdit, onDelete }) => {
  const typeColor = PartnerTypeColors[partner.partnerType as PartnerType] || '#6b7280';
  const typeLabel = PartnerTypeLabels[partner.partnerType as PartnerType] || partner.partnerType;

  return (
    <div className="page-card" style={{ borderLeft: `4px solid ${typeColor}` }}>
      <div className="page-card-header">
        <div>
          <h3>{partner.name}</h3>
          <span className="page-badge" style={{ backgroundColor: typeColor }}>{typeLabel}</span>
        </div>
        <div className="page-card-actions">
          <button className="page-btn-icon edit" onClick={() => onEdit(partner)} title="Bearbeiten"><Pencil size={14} /></button>
          <button className="page-btn-icon delete" onClick={() => onDelete(partner)} title="Löschen"><Trash2 size={14} /></button>
        </div>
      </div>
      {partner.description && <p className="page-card-description">{partner.description}</p>}
      <div className="page-card-body">
        {partner.website && <div><Globe size={12} /> <a href={partner.website} target="_blank" rel="noopener noreferrer">{partner.website}</a></div>}
        {partner.contactEmail && <div><Mail size={12} /> <a href={`mailto:${partner.contactEmail}`}>{partner.contactEmail}</a></div>}
        {partner.contactPhone && <div><Phone size={12} /> <a href={`tel:${partner.contactPhone}`}>{partner.contactPhone}</a></div>}
        {partner.address && <div><MapPin size={12} /> {partner.address}</div>}
      </div>
      <div className="page-card-footer">
        <span className="page-badge" style={{ backgroundColor: partner.isActive ? '#28a745' : '#dc3545' }}>
          {partner.isActive ? 'Aktiv' : 'Inaktiv'}
        </span>
        <span className="page-table-meta">Erstellt: {new Date(partner.createdAt).toLocaleDateString('de-DE')}</span>
      </div>
    </div>
  );
};

// Partner List Item Component
const PartnerListItem: React.FC<{
  partner: CustomPartner;
  onEdit: (partner: CustomPartner) => void;
  onDelete: (partner: CustomPartner) => void;
}> = ({ partner, onEdit, onDelete }) => {
  const typeColor = PartnerTypeColors[partner.partnerType as PartnerType] || '#6b7280';
  const typeLabel = PartnerTypeLabels[partner.partnerType as PartnerType] || partner.partnerType;

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <h3>{partner.name}</h3>
          <div className="page-card-badges">
            <span className="page-badge" style={{ backgroundColor: typeColor }}>{typeLabel}</span>
            <span className="page-badge" style={{ backgroundColor: partner.isActive ? '#28a745' : '#dc3545' }}>
              {partner.isActive ? 'Aktiv' : 'Inaktiv'}
            </span>
          </div>
        </div>
        <div className="page-card-actions">
          <button className="page-btn-icon edit" onClick={() => onEdit(partner)} title="Bearbeiten"><Pencil size={14} /></button>
          <button className="page-btn-icon delete" onClick={() => onDelete(partner)} title="Löschen"><Trash2 size={14} /></button>
        </div>
      </div>
      {partner.description && <p className="page-card-description">{partner.description}</p>}
      <div className="page-card-body">
        {partner.contactEmail && <span><Mail size={12} /> {partner.contactEmail}</span>}
        {partner.contactPhone && <span><Phone size={12} /> {partner.contactPhone}</span>}
        {partner.website && <span><Globe size={12} /> Website</span>}
      </div>
    </div>
  );
};

export default CustomPartners;
